import { useAuth } from "@features/auth";
import { useGlobalUserProfile } from "@shared/hooks/useGlobalUser";
import { useTenantId, useUserName } from "@shared/selectors/globalSelectors";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@shared/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@shared/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { LogOut, User, X } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import {
  useDeleteAllSessions,
  useDeleteSessions,
  useSessions,
  useUtility,
} from "../../hooks";
import { bentomenu as Bentomenu } from "../bentomenu/bentomenu";
import { TopnavSkeleton } from "./topnav-skeleton";
interface topnavProps {
  // Add your props here
}

export const topnav: React.FC<topnavProps> = () => {
  const name = useUserName();
  const [bentoOpen, setBentoOpen] = useState(false);
  const { userProfile, isProfileLoading, logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { data: userProfileData } = useGlobalUserProfile();
  const { data: sessions } = useSessions();

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const bentoButtonRef = useRef<HTMLButtonElement>(null);
  const bentoRef = useRef<HTMLDivElement>(null);

  // Get tenant ID from user profile
  // const tenantId = userProfile?.utility?.tenant?.id;
  const tenantId = useTenantId();
  const { mutateAsync: deleteSession, isPending: isLoading } =
    useDeleteSessions();
  const { mutateAsync: deleteAllSession, isPending: isAllLoading } =
    useDeleteAllSessions();
  const [loggingOutSessionId, setLoggingOutSessionId] = useState<
    string | number | null
  >(null);

  // Use the utility hook with tenant ID
  const {
    utilities,
    selectedUtility,
    setSelectedUtility,
    loading: utilitiesLoading,
    clearUtility,
  } = useUtility(tenantId);

  // Hover handlers for bento menu
  const handleBentoHoverEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setBentoOpen(true);
  }, []);

  const handleBentoHoverLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setBentoOpen(false);
    }, 100); // Small delay to prevent flickering when moving between button and menu
  }, []);

  const handleBentoMenuEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);

      // Clear utility data on logout
      clearUtility();

      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getInitials = (fullName: string): string => {
    if (!fullName) return "";

    const names = fullName.trim().split(" ");
    const initials = names
      .slice(0, 2) // take first two words (e.g., first name and last name)
      .map((n) => n.charAt(0).toUpperCase())
      .join("");

    return initials;
  };

  // Show loading skeleton while data is being fetched
  if (utilitiesLoading || isProfileLoading) {
    return <TopnavSkeleton />;
  }

  const handleSessionLogout = async (sessionId: string) => {
    setLoggingOutSessionId(sessionId);

    await deleteSession({ id: sessionId });
  };

  const handleAllSessionLogout = async () => {
    await deleteAllSession();
  };
  return (
    <header className="w-full z-50 bg-white shadow-sm flex items-center justify-between px-6 h-16">
      <div className="flex items-center gap-2 -ml-[0.8rem]">
        <button
          ref={bentoButtonRef}
          className="flex items-center p-1.5 rounded hover:bg-blue-50 mr-1"
          onClick={handleBentoHoverEnter}
          aria-label="Open bento menu"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="5" cy="5" r="2" fill="#111827" />
            <circle cx="14" cy="5" r="2" fill="#111827" />
            <circle cx="23" cy="5" r="2" fill="#111827" />
            <circle cx="5" cy="14" r="2" fill="#111827" />
            <circle cx="14" cy="14" r="2" fill="#111827" />
            <circle cx="23" cy="14" r="2" fill="#111827" />
            <circle cx="5" cy="23" r="2" fill="#111827" />
            <circle cx="14" cy="23" r="2" fill="#111827" />
            <circle cx="23" cy="23" r="2" fill="#111827" />
          </svg>
        </button>
        <span className="text-xl font-bold flex items-center">
          <span className="text-black">SMART</span>
          <span className="text-blue-600">360</span>
        </span>
      </div>

      <div className="flex items-center gap-4">
        {/* Utility Selector */}
        <Select
          value={selectedUtility || ""}
          onValueChange={setSelectedUtility}
          disabled={utilities.length === 0}
        >
          <SelectTrigger className="w-fit">
            <SelectValue placeholder="Select utility" />
          </SelectTrigger>
          <SelectContent>
            {utilities.map((utility) => (
              <SelectItem key={utility.id} value={utility.name}>
                {utility.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* User Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="focus:outline-none">
              <Avatar>
                <AvatarFallback>
                  {name ? getInitials(name) : "U"}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              className="flex items-center gap-2 text-red-600"
              asChild
            >
              <AlertDialog>
                <AlertDialogTrigger className="flex w-full items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Profile & Sessions</span>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                  <AlertDialogCancel className="absolute top-3 right-3 text-gray-800 hover:text-gray-900 text-xl font-bold">
                    <X className="w-5 h-5" />
                  </AlertDialogCancel>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-xl font-semibold">
                      Profile & Login Sessions
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Manage your profile information and active login sessions
                    </AlertDialogDescription>
                  </AlertDialogHeader>

                  {/* Profile Info Card */}
                  <div className="border rounded-lg p-4 bg-muted/50 space-y-3">
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center text-lg font-medium text-gray-700">
                        {name ? getInitials(name) : "U"}
                      </div>
                      <div>
                        <div className="font-medium text-lg">{name}</div>
                        <div className="text-sm text-muted-foreground">
                          {userProfileData?.user?.email}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium text-muted-foreground mb-1">
                        Departments
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {
                          <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-600 text-xs font-medium">
                            {userProfileData?.extraDataMap?.department}
                          </span>
                        }
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium text-muted-foreground mb-1">
                        Roles
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {userProfileData?.roleCode.map((role) => (
                          <span
                            key={role}
                            className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-medium"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Active Sessions */}
                  <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium text-base">
                          Active Sessions
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Manage your active login sessions across different
                          devices
                        </div>
                      </div>
                      <button
                        className="border rounded-md px-3 py-1.5 text-sm font-medium hover:bg-muted"
                        onClick={() => handleAllSessionLogout()}
                      >
                        <span className="flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 16l4-4m0 0l-4-4m4 4H7"
                            />
                          </svg>
                          Logout All Others
                        </span>
                      </button>
                    </div>

                    {/* Session Items */}
                    {sessions?.map((session, idx) => (
                      <div
                        key={idx}
                        className="border rounded-md p-3 bg-white flex justify-between items-center"
                      >
                        <div>
                          <div className="font-medium">{session.device}</div>

                          <div className="text-sm text-muted-foreground">
                            {session.lastActivitySince ?? "NA"} ago
                          </div>
                        </div>
                        {session.isCurrentSession ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 font-medium">
                            Current
                          </span>
                        ) : (
                          <button
                            className="border rounded-md px-3 py-1 text-sm font-medium hover:bg-muted"
                            onClick={() => handleSessionLogout(session.id)}
                          >
                            {loggingOutSessionId === session.id && isLoading
                              ? "Logging out..."
                              : "Logout"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Close Button */}
                  <AlertDialogFooter>
                    <AlertDialogCancel className="w-full">
                      Close
                    </AlertDialogCancel>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2 text-red-600"
              asChild
            >
              <AlertDialog>
                <AlertDialogTrigger className="flex w-full items-center gap-2">
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you sure you want to logout?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This will end your current session and you'll need to
                      login again to access your account.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                    >
                      {isLoggingOut ? "Logging out..." : "Logout"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {bentoOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          onClick={() => setBentoOpen(false)}
          aria-hidden="true"
        />
      )}
      <div
        ref={bentoRef}
        onMouseEnter={handleBentoMenuEnter}
        className="absolute top-16 left-0 z-50"
      >
        <Bentomenu open={bentoOpen} onClose={() => setBentoOpen(false)} />
      </div>
    </header>
  );
};
